<template>
  <v-container class="modern-view pb-12">
    <!-- Premium Header -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-boxes</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Facturación por <span class="secondary--text">Producto</span> (Relbase)</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Análisis detallado de ventas emitidas vía Relbase</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <export-relbase-product-billing-report :filter="filter" :search="search" :options="options" :disabled="loading || !filter.date_after"/>
      </div>
    </v-row>

    <!-- Filters Card -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Fecha Factura Desde</div>
          <v-text-field 
            hide-details 
            dense 
            v-model='filter.date_after' 
            outlined 
            type="date" 
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Fecha Factura Hasta</div>
          <v-text-field 
            hide-details 
            dense 
            v-model='filter.date_before' 
            outlined 
            type="date" 
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Filtrar por SKU</div>
          <v-text-field
            v-model="filter.sku"
            placeholder="Ej: ABC-123"
            outlined
            dense
            hide-details
            class="modern-input"
            clearable
          />
        </v-col>
        <v-col cols="12" md="3">
           <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Buscar Producto / Cliente</div>
           <v-text-field
             v-model="search"
             placeholder="Palabra Clave"
             outlined
             dense
             hide-details
             prepend-inner-icon="fas fa-search"
             class="modern-input"
             clearable
           />
        </v-col>
      </v-row>
    </v-card>

    <!-- Data Table -->
    <v-card outlined class="modern-card shadow-sm border-0 overflow-hidden">
      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        class="modern-table"
        :footer-props="{ 'items-per-page-options': [20, 50, 100] }"
      >
        <template v-slot:item.sku="{ item }">
          <span class="caption font-weight-bold grey--text text-uppercase">{{ item.sku || 'S/SKU' }}</span>
        </template>
        <template v-slot:item.name="{ item }">
          <span class="font-weight-bold primary--text">{{ item.name }}</span>
        </template>
        <template v-slot:item.precio_unitario_neto="{ item }">
          {{ item.precio_unitario_neto | moneda }}
        </template>
        <template v-slot:item.total_neto_sum="{ item }">
          <span class="font-weight-bold primary--text">{{ item.total_neto_sum | moneda }}</span>
        </template>
        <template v-slot:item.total_iva="{ item }">
          {{ item.total_iva | moneda }}
        </template>
        <template v-slot:item.total_bruto="{ item }">
          <span class="font-weight-black">{{ item.total_bruto | moneda }}</span>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { fetchRelbaseProductBillingReport } from '@/api/relbase'
import ExportRelbaseProductBillingReport from '@/components/relbase/ExportRelbaseProductBillingReport.vue'
import debounce from 'lodash/debounce'

export default {
  name: 'RelbaseProductBillingReport',
  components: { ExportRelbaseProductBillingReport },
  data() {
    return {
      items: [],
      totalItems: 0,
      loading: false,
      search: '',
      options: {
        page: 1,
        itemsPerPage: 20,
        sortBy: ['total_bruto'],
        sortDesc: [true]
      },
      filter: {
        date_after: '',
        date_before: '',
        sku: '',
        customer: null,
        seller: null,
        category: null
      },
      headers: [
        { text: 'SKU', value: 'sku' },
        { text: 'Producto', value: 'name' },
        { text: 'Cantidad', value: 'total_qty', align: 'center' },
        { text: 'P. Unitario Neto', value: 'precio_unitario_neto', align: 'end' },
        { text: 'Neto Facturado', value: 'total_neto_sum', align: 'end' },
        { text: 'IVA', value: 'total_iva', align: 'end' },
        { text: 'Total Bruto', value: 'total_bruto', align: 'end' }
      ]
    }
  },
  watch: {
    options: { handler() { this.fetchData() }, deep: true },
    filter: { handler() { this.options.page = 1; this.fetchData() }, deep: true },
    search: { 
      handler: debounce(function() {
        this.options.page = 1
        this.fetchData()
      }, 500)
    }
  },
  methods: {
    async fetchData() {
      this.loading = true
      const { page, itemsPerPage, sortBy, sortDesc } = this.options
      
      const sortField = (sortBy && sortBy.length) ? sortBy[0] : 'total_bruto'
      const ordering = (sortDesc && sortDesc[0]) ? `-${sortField}` : sortField

      const params = {
        page,
        page_size: itemsPerPage,
        ordering,
        search: this.search,
        date_emited_after: this.filter.date_after,
        date_emited_before: this.filter.date_before,
        sku: this.filter.sku,
        customer: this.filter.customer,
        seller: this.filter.seller,
        category: this.filter.category
      }
      
      try {
        const data = await fetchRelbaseProductBillingReport(params)
        this.items = data.results || []
        this.totalItems = data.count || 0
      } catch (e) {
        console.error("Error fetching Relbase Product report:", e)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.modern-view { font-family: 'Outfit', sans-serif !important; }
.modern-card { border-radius: 16px !important; border-color: rgba(0,0,0,0.06) !important; }
.border-white-sm { border: 3px solid white !important; }
.shadow-sm { box-shadow: 0 4px 20px rgba(0,0,0,0.04) !important; }
.lh-1 { line-height: 1 !important; }
.tracking-widest { letter-spacing: 1.5px; }
.modern-input >>> .v-input__control >>> .v-input__slot { border-radius: 10px !important; background-color: #f8fafc !important; }
.modern-table >>> thead th { background-color: #f8fafc !important; color: #64748b !important; text-transform: uppercase !important; font-size: 11px !important; font-weight: 800 !important; letter-spacing: 1px !important; }
</style>
